import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Gift, Coffee, Sparkles, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import useSEO from "../lib/useSEO";
import useReveal from "../lib/useReveal";
import SectionHeading from "../components/SectionHeading";
import Marquee from "../components/Marquee";
import { IMG } from "../lib/media";

const CTA_TO = {
  "Join the club": "/order",
  "Get stamping": "/visit",
  "Plan a visit": "/visit",
  "Order direct": "/order",
  "Enquire wholesale": "/wholesale#enquire",
};

export default function Offers() {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [signupLoading, setSignupLoading] = useState(false);
  const [signedUp, setSignedUp] = useState(false);
  const ref = useReveal([offers, signedUp]);

  useSEO({
    title: "Offers & Loyalty — SugaSpice Bakery & Kitchen",
    description:
      "Direct-only deals, loyalty stamps, weekly meal offers and the SugaSpice Founders Club — save money ordering direct in St Neots.",
    image: IMG.burger_dark,
  });

  useEffect(() => {
    api
      .get("/offers")
      .then((r) => setOffers(r.data.offers || []))
      .catch(() => toast.error("Couldn't load offers."))
      .finally(() => setLoading(false));
  }, []);

  const join = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSignupLoading(true);
    try {
      await api.post("/newsletter/subscribe", { email, source: "loyalty" });
      setSignedUp(true);
      toast.success("You're in the Founders Club. Welcome perks heading to your inbox.");
      setEmail("");
    } catch {
      toast.error("Couldn't sign you up. Please try again.");
    } finally {
      setSignupLoading(false);
    }
  };

  return (
    <div ref={ref}>
      {/* HERO */}
      <section className="bg-[#C8102E] text-[#F7F5F0] overflow-hidden">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16 pt-12 md:pt-16 pb-20 md:pb-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-7 fade-up">
              <span className="text-[11px] font-semibold tracking-[0.28em] uppercase text-[#F7F5F0]/80">Offers & loyalty</span>
              <h1 className="mt-5 font-display text-[clamp(2.8rem,7vw,7rem)] leading-[0.88] tracking-tight uppercase">
                Save more.<br />
                <span className="text-[#1A1A1A]">Eat more.</span>
              </h1>
              <p className="mt-6 max-w-xl text-base md:text-lg text-[#F7F5F0]/90 leading-relaxed">
                Direct-only deals, weekly drops and the SugaSpice Founders Club. Order direct, save vs the delivery apps, and earn perks while you're at it.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <a href="#join" className="btn-pill bg-[#F7F5F0] text-[#1A1A1A] hover:bg-white" data-testid="offers-hero-join">
                  Join Founders Club <ArrowUpRight className="h-4 w-4" />
                </a>
                <Link to="/order" className="btn-pill border-2 border-[#F7F5F0]/40 text-[#F7F5F0] hover:bg-[#F7F5F0] hover:text-[#1A1A1A]">
                  Order Direct
                </Link>
              </div>
            </div>
            <div className="lg:col-span-5 fade-up">
              <div className="aspect-square rounded-3xl overflow-hidden shadow-[0_30px_60px_rgba(0,0,0,0.3)]">
                <img loading="lazy" decoding="async" src={IMG.burger_dark} alt="SugaSpice offers" className="w-full h-full object-cover" />
              </div>
            </div>
          </div>
        </div>
      </section>

      <Marquee items={["Direct-Only Deals", "Loyalty Stamps", "Weekly Drops", "Founders Club", "Save vs the Apps", "Local & Generous"]} theme="dark" />

      {/* CURRENT OFFERS */}
      <section className="py-24 md:py-32 bg-[#F7F5F0]">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16">
          <SectionHeading eyebrow="Live offers" title="What's on right now." intro="Updated every week. Order direct or pop in to redeem." />
          {loading ? (
            <div className="flex items-center justify-center py-20 text-[#5C5C5C]">
              <Loader2 className="h-6 w-6 animate-spin mr-3" /> Loading offers…
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 mt-12">
              {offers.map((o, i) => (
                <div
                  key={o.id}
                  data-testid={`offer-card-${i}`}
                  className="fade-up group relative bg-white rounded-3xl p-7 border border-[#D1C9BE]/60 flex flex-col hover:-translate-y-1 hover:shadow-[0_30px_60px_rgba(0,0,0,0.1)] transition-all duration-300"
                  style={{ transitionDelay: `${i * 50}ms` }}
                >
                  <div className="flex items-start justify-between gap-3 mb-4">
                    <span className="text-[11px] tracking-[0.22em] uppercase font-semibold text-[#C8102E]">{o.subtitle}</span>
                    <span className="text-[10px] tracking-[0.16em] uppercase font-semibold px-2.5 py-1 rounded-full bg-[#1A1A1A] text-[#F7F5F0]">{o.badge}</span>
                  </div>
                  <h3 className="font-display text-2xl md:text-[1.6rem] uppercase tracking-tight leading-tight">{o.title}</h3>
                  <p className="mt-4 text-sm text-[#5C5C5C] leading-relaxed flex-1">{o.body}</p>
                  <Link to={CTA_TO[o.cta] || "/order"} className="mt-6 inline-flex items-center gap-2 text-sm font-semibold tracking-[0.16em] uppercase text-[#1A1A1A] group-hover:text-[#C8102E] transition-colors" data-testid={`offer-cta-${i}`}>
                    {o.cta} <ArrowUpRight className="h-4 w-4" />
                  </Link>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* HOW LOYALTY WORKS */}
      <section className="py-24 md:py-32 bg-[#1A1A1A] text-[#F7F5F0]">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16">
          <SectionHeading eyebrow="The loyalty scheme" title="Three ways to save with SugaSpice." invert />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-14">
            {[
              {
                icon: Coffee,
                title: "Stamp every coffee",
                body: "Every bean-to-cup coffee earns a digital stamp. 10 stamps = your next one on us. No card to lose.",
              },
              {
                icon: Gift,
                title: "Direct order rewards",
                body: "Every direct order earns points. Hit milestones for free fries, free strips and a free burger once a quarter.",
              },
              {
                icon: Sparkles,
                title: "Founders Club perks",
                body: "Welcome 15% off your first 3 direct orders, birthday treat, early access to new menu drops.",
              },
            ].map((p, i) => (
              <div key={p.title} className="fade-up bg-[#262626] rounded-3xl p-7" style={{ transitionDelay: `${i * 60}ms` }} data-testid={`loyalty-pillar-${i}`}>
                <div className="h-11 w-11 rounded-full bg-[#C8102E] flex items-center justify-center">
                  <p.icon className="h-5 w-5 text-[#F7F5F0]" />
                </div>
                <h4 className="mt-5 font-display text-xl md:text-2xl uppercase tracking-tight">{p.title}</h4>
                <p className="mt-3 text-sm text-[#C9C5BE] leading-relaxed">{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* STAMP CARD VISUAL */}
      <section className="py-24 md:py-28 bg-[#E8E3DB]">
        <div className="mx-auto max-w-[1100px] px-6 md:px-12 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-5 fade-up">
              <SectionHeading eyebrow="Coffee stamp card" title="Buy 9, your 10th is on us." intro="Tracked in the SugaSpice app — no plastic card to lose. Every bean-to-cup counts." />
              <Link to="/visit" className="btn-pill mt-8 bg-[#C8102E] text-[#F7F5F0] hover:bg-[#A50D24]" data-testid="stamp-card-visit">
                Come and earn one <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
            <div className="lg:col-span-7 fade-up">
              <div className="bg-[#F7F5F0] rounded-3xl p-7 md:p-9 border border-[#D1C9BE]/60 shadow-[0_20px_40px_rgba(0,0,0,0.06)]">
                <div className="flex items-center justify-between mb-6">
                  <p className="text-[11px] tracking-[0.22em] uppercase font-semibold text-[#C8102E]">SugaSpice Stamp Card</p>
                  <p className="text-[11px] tracking-[0.18em] uppercase text-[#5C5C5C]">9 / 10</p>
                </div>
                <div className="grid grid-cols-5 gap-3 md:gap-4">
                  {Array.from({ length: 10 }).map((_, i) => (
                    <div key={i} className={`aspect-square rounded-full flex items-center justify-center border-2 ${i < 9 ? "bg-[#C8102E] border-[#C8102E] text-[#F7F5F0]" : "bg-transparent border-[#D1C9BE] text-[#D1C9BE]"}`}>
                      <Coffee className="h-5 w-5 md:h-6 md:w-6" />
                    </div>
                  ))}
                </div>
                <p className="mt-6 text-xs text-[#5C5C5C] tracking-wide">One more coffee and the next one's free — that's the deal.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* JOIN FOUNDERS CLUB */}
      <section id="join" className="py-24 md:py-32 bg-[#F7F5F0]">
        <div className="mx-auto max-w-[1100px] px-6 md:px-12 lg:px-16">
          {signedUp ? (
            <div className="bg-[#1A1A1A] text-[#F7F5F0] rounded-3xl p-10 md:p-14 text-center fade-up" data-testid="offers-signup-success">
              <CheckCircle2 className="h-12 w-12 text-[#C8102E] mx-auto" />
              <h3 className="font-display text-4xl uppercase tracking-tight mt-6">Welcome to the club.</h3>
              <p className="mt-4 text-[#C9C5BE] max-w-md mx-auto">Your welcome perk is on its way. Direct-order discount code lands in your inbox within minutes.</p>
              <button onClick={() => setSignedUp(false)} className="btn-pill mt-8 bg-[#C8102E] text-[#F7F5F0] hover:bg-[#A50D24]" data-testid="offers-signup-reset">
                Done
              </button>
            </div>
          ) : (
            <div className="bg-[#1A1A1A] text-[#F7F5F0] rounded-3xl p-8 md:p-12 fade-up" data-testid="offers-signup-card">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
                <div className="lg:col-span-7">
                  <span className="text-[11px] font-semibold tracking-[0.28em] uppercase text-[#C8102E]">Founders Club</span>
                  <h3 className="mt-4 font-display text-3xl md:text-5xl uppercase tracking-tight leading-[0.95]">
                    15% off your first<br />3 direct orders.
                  </h3>
                  <p className="mt-5 text-[#C9C5BE] max-w-md">Pop your email in below and we'll send your welcome discount code. Plus offer drops, birthday treats and early access to new menu items. No spam.</p>
                </div>
                <div className="lg:col-span-5">
                  <form onSubmit={join} className="flex flex-col gap-3" data-testid="offers-signup-form">
                    <input
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@email.com"
                      className="bg-transparent border border-white/20 rounded-full px-6 py-4 text-sm placeholder:text-[#7a7a7a] focus:border-[#C8102E] focus:outline-none"
                      data-testid="offers-signup-input"
                    />
                    <button type="submit" disabled={signupLoading} className="btn-pill bg-[#C8102E] text-[#F7F5F0] hover:bg-[#A50D24] disabled:opacity-60" data-testid="offers-signup-submit">
                      {signupLoading ? "Joining…" : "Join the Club"} <ArrowUpRight className="h-4 w-4" />
                    </button>
                  </form>
                  <p className="mt-3 text-[10px] tracking-[0.18em] uppercase text-[#7a7a7a]">By joining you agree to receive offer emails. Unsubscribe anytime.</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
